/**
 * Hybrid Checkbox component.
 * Automatically chooses between the Tailwind/CSS implementation (preferred —
 * no styled-components runtime) and the styled-components implementation
 * (when styled-system props or a `styled(Checkbox)` extension are detected).
 */

import * as React from "react";
import { needsStyledComponents } from "@sproutsocial/seeds-react-system-props";
import type { TypeCheckboxProps } from "./types/CheckboxTypes";
import CheckboxStyledComponents from "./CheckboxStyledComponents";
import { CheckboxTailwind } from "./CheckboxTailwind";

const CheckboxHybrid = React.forwardRef<HTMLInputElement, TypeCheckboxProps>(
  (props, ref) => {
    // Gate on `needsStyledComponents` (not just `hasStyledProps`) so that a bare
    // `styled(Checkbox)` extension — which injects `theme`/`forwardedComponent`
    // but no styled-system props — still renders the styled-components path and
    // keeps its SC styles. `needsStyledComponents` (via `isStyledExtension`)
    // already detects the injected `sc-...` className, so no extra class check is
    // needed here. This is an intentional deviation from ButtonHybrid, which
    // gates only on `hasStyledProps`.
    //
    // `appearance="pill"` also routes here: the pill flow is unsupported on the
    // Tailwind path (removed per review), so it renders via the legacy
    // styled-components component, which still implements pill. This keeps the
    // public `appearance` type working with no silent default-box regression.
    if (props.appearance === "pill" || needsStyledComponents(props)) {
      // Forward the ref on the SC path too, restoring the pre-migration instance
      // ref (pre-migration `Checkbox` was the class, so `styled(Checkbox)` /
      // `<Checkbox ref={r}/>` resolved `r` to the class instance). Mirrors
      // ButtonHybrid forwarding `ref || innerRef` on both paths.
      return (
        <CheckboxStyledComponents
          {...props}
          ref={ref as unknown as React.Ref<CheckboxStyledComponents>}
        />
      );
    }

    // Use the Tailwind version (preferred - better performance)
    return <CheckboxTailwind {...props} ref={ref} />;
  }
);

CheckboxHybrid.displayName = "Checkbox";
export default CheckboxHybrid;
